import React from 'react'
import { useField } from 'formik'
import {
  CheckboxGroup,
  CheckboxGroupProps,
  FileUpload,
  FileUploadProps,
  Input,
  InputProps,
  Password,
  RadioGroup,
  RadioGroupProps,
  TextArea,
  TextAreaProps
} from '../../atoms'

import { Icon } from '@iconify/react'

import styles from './_.module.css'

interface FieldProps {
  name: string
  error?: boolean
  label?: React.ReactNode | string
  helperText?: React.ReactNode | string
  className?: string
  showError?: boolean
  onBlur?: React.EventHandler<React.FocusEvent>
  required?: boolean
}

/* text */
interface TextFieldProps extends FieldProps {
  fieldProps?: InputProps
}

export const TextField: React.FC<TextFieldProps> = ({
  className = '',
  label,
  name,
  helperText,
  showError = true,
  required,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Input {...field} {...fieldProps} className={errorClass} />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* password */
interface PasswordFieldProps extends FieldProps {
  fieldProps?: InputProps
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  const errorClass = meta.touched && meta.error ? styles.borderRed : ''

  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <Password {...field} {...fieldProps} className={errorClass} />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />{' '}
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* checkbox */
interface CheckboxFieldProps extends FieldProps {
  fieldProps?: CheckboxGroupProps
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  setFieldValue,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <CheckboxGroup
          onChange={(values): void => setFieldValue(name, values)}
          value={field.value}
          {...fieldProps}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />{' '}
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* textarea */
interface TextAreaFieldProps extends FieldProps {
  fieldProps?: TextAreaProps
  setFieldTouched: (field: string, value: any, shouldValidate?: boolean) => void
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  setFieldTouched,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <TextArea
          {...field}
          {...fieldProps}
          onBlur={(): void => setFieldTouched(name, true)}
        />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />{' '}
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* radio field */
interface RadioFieldProps extends FieldProps {
  fieldProps: RadioGroupProps
}

export const RadioField: React.FC<RadioFieldProps> = ({
  className = '',
  label,
  name,
  showError = true,
  helperText,
  required,
  fieldProps
}) => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
        <RadioGroup {...field} {...fieldProps} />
      </label>
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {showError && meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />{' '}
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}

/* file */
interface FileUploadFieldProps extends FieldProps {
  fieldProps: Partial<FileUploadProps>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

export const FileUploadField = ({
  className = '',
  name,
  label,
  helperText,
  required,
  setFieldValue,
  fieldProps
}: FileUploadFieldProps): React.ReactElement => {
  const [field, meta] = useField(name)
  return (
    <div className={className}>
      <label>
        <span className={styles.labelText}>
          {label}
          {required && <span className={styles.requiredText}>*</span>}
        </span>
      </label>
      <FileUpload
        {...fieldProps}
        handleFiles={(files): void => setFieldValue(name, files)}
        defaultFileList={field.value}
      />
      {helperText && <span className={styles.helperText}>{helperText}</span>}
      {meta.touched && meta.error ? (
        <div className={styles.error}>
          <Icon
            icon="ant-design:close-circle-filled"
            className={styles.errorIcon}
          />{' '}
          {meta.error}
        </div>
      ) : null}
    </div>
  )
}
